import { tKey } from 'ember-intl';
import { PRODUCT_NAMES } from 'frontend-utils/utils/constants/constant-values';
import MenuLogoChannelIllustration from 'lumen/assets/images/central-mapping/menu-logo-channel.svg?raw';
import MenuLogoCompanyIllustration from 'lumen/assets/images/central-mapping/menu-logo-company.svg?raw';
import MenuLogoDayOfWeekIllustration from 'lumen/assets/images/central-mapping/menu-logo-day-of-week.svg?raw';
import MenuLogoGuestCountryIllustration from 'lumen/assets/images/central-mapping/menu-logo-guest-country.svg?raw';
import MenuLogoMarketSegmentIllustration from 'lumen/assets/images/central-mapping/menu-logo-market-segment.svg?raw';
import MenuLogoOriginIllustration from 'lumen/assets/images/central-mapping/menu-logo-origin.svg?raw';
import MenuLogoRateCodeIllustration from 'lumen/assets/images/central-mapping/menu-logo-rate-code.svg?raw';
import MenuLogoRoomCategoriesIllustration from 'lumen/assets/images/central-mapping/menu-logo-room-categories.svg?raw';
import MenuLogoRoomTypeIllustration from 'lumen/assets/images/central-mapping/menu-logo-room-type.svg?raw';
import MenuLogoSourceIllustration from 'lumen/assets/images/central-mapping/menu-logo-source.svg?raw';
import MenuLogoStayRestrictionRateGroupIllustration from 'lumen/assets/images/central-mapping/menu-logo-stay-restriction-rate-group.svg?raw';
import MenuLogoTravelAgentIllustration from 'lumen/assets/images/central-mapping/menu-logo-travel-agent.svg?raw';
import type {
  CentralMappingMappingEntry,
  CentralMappingMappingEntryDayOfWeek,
  CentralMappingMappingEntryMarketSegment,
  CentralMappingMappingEntryRoomType,
} from 'lumen/models';

export const PMS_VALUE_TYPES = {
  MARKET_CODE: 'market_code',
  RATE_CODE: 'rate_code',
  ROOM_TYPE: 'room_type',
  PRICE_PUSHING_IDENTIFIER: 'price_pushing_identifier',
  TRAVEL_AGENT: 'travel_agent',
  GUEST_COUNTRY: 'guest_country',
  COMPANY: 'company',
  DAY_OF_WEEK: 'day_of_week',
  SOURCE: 'source',
  ORIGIN: 'origin',
} as const;
export type PmsValueType =
  (typeof PMS_VALUE_TYPES)[keyof typeof PMS_VALUE_TYPES];
export const isPmsValueType = (value: string): value is PmsValueType => {
  return Object.values(PMS_VALUE_TYPES).includes(value as PmsValueType);
};

export const DEFAULT_VALUE_TYPE = PMS_VALUE_TYPES.MARKET_CODE;

export const PMS_VALUE_TYPE_TRANSLATIONS = {
  [PMS_VALUE_TYPES.MARKET_CODE]: tKey('settings.central_mapping.market_codes'),
  [PMS_VALUE_TYPES.RATE_CODE]: tKey('settings.central_mapping.rate_codes'),
  [PMS_VALUE_TYPES.ROOM_TYPE]: tKey('settings.central_mapping.room_type'),
  [PMS_VALUE_TYPES.PRICE_PUSHING_IDENTIFIER]: tKey(
    'settings.central_mapping.price_pushing_identifier',
  ),
  [PMS_VALUE_TYPES.TRAVEL_AGENT]: tKey('settings.central_mapping.travel_agent'),
  [PMS_VALUE_TYPES.GUEST_COUNTRY]: tKey(
    'settings.central_mapping.guest_country',
  ),
  [PMS_VALUE_TYPES.COMPANY]: tKey('settings.central_mapping.company'),
  [PMS_VALUE_TYPES.DAY_OF_WEEK]: tKey('settings.central_mapping.day_of_week'),
  [PMS_VALUE_TYPES.SOURCE]: tKey('settings.central_mapping.source'),
  [PMS_VALUE_TYPES.ORIGIN]: tKey('settings.central_mapping.origin'),
} as const;

export const BREAKDOWNS = {
  MARKET_SEGMENT: 'market_segment',
  CHANNEL: 'channel',
  ROOM_TYPE: 'room_type',
  RATE_CODE: 'rate_code',
  TRAVEL_AGENT: 'travel_agent',
  GUEST_COUNTRY: 'guest_country',
  COMPANY: 'company',
  DAY_OF_WEEK: 'day_of_week',
  SOURCE: 'source',
  ORIGIN: 'origin',
  CHANNEL_ROOM_CATEGORIES: 'competitor_roomtype',
  BENCHMARK_MARKET_SEGMENT: 'benchmark_market_segment',
  STAY_RESTRICTION_RATE_GROUP: 'stay_restriction_rate_group',
} as const;
export type Breakdown = (typeof BREAKDOWNS)[keyof typeof BREAKDOWNS];

export const MAPPING_BREAKDOWNS = {
  MARKET_SEGMENT: 'market_segment',
  CHANNEL: 'channel',
  ROOM_TYPE: 'room_type',
  RATE_CODE: 'rate_code',
  TRAVEL_AGENT: 'travel_agent',
  GUEST_COUNTRY: 'guest_country',
  COMPANY: 'company',
  DAY_OF_WEEK: 'day_of_week',
  SOURCE: 'source',
  ORIGIN: 'origin',
  STAY_RESTRICTION_RATE_GROUP: 'stay_restriction_rate_group',
} as const;
export type MappingBreakdowns =
  (typeof MAPPING_BREAKDOWNS)[keyof typeof MAPPING_BREAKDOWNS];

export const isMappingBreakdown = (
  value: string,
): value is MappingBreakdowns => {
  return Object.values(MAPPING_BREAKDOWNS).includes(value as MappingBreakdowns);
};

export const DEFAULT_BREAKDOWN = MAPPING_BREAKDOWNS.MARKET_SEGMENT;

export const DEFAULT_PAGE_SIZE = 5000;

export const PAGE_NUMBER_OPTIONS = [100, 1000, 5000, 10000] as const;

export const DEFAULT_PAGE = 1;

// The largest hotel in the world has 7352 rooms, so 100000 seems enough for a long while.
export const MAX_ROOM_INVENTORY = 100000;

// 100 adults in a room seems enough.
export const MAX_ADULTS_IN_ROOM = 100;

export const NUMERIC_VALUE_KEYS = ['inventory', 'maxAdults'] as const;

export type NumericValueKeysTypes = (typeof NUMERIC_VALUE_KEYS)[number];

export const BREAKDOWN_LOGOS: Record<Breakdown, string> = {
  [BREAKDOWNS.MARKET_SEGMENT]: MenuLogoMarketSegmentIllustration,
  [BREAKDOWNS.CHANNEL]: MenuLogoChannelIllustration,
  [BREAKDOWNS.ROOM_TYPE]: MenuLogoRoomTypeIllustration,
  [BREAKDOWNS.RATE_CODE]: MenuLogoRateCodeIllustration,
  [BREAKDOWNS.TRAVEL_AGENT]: MenuLogoTravelAgentIllustration,
  [BREAKDOWNS.GUEST_COUNTRY]: MenuLogoGuestCountryIllustration,
  [BREAKDOWNS.COMPANY]: MenuLogoCompanyIllustration,
  [BREAKDOWNS.DAY_OF_WEEK]: MenuLogoDayOfWeekIllustration,
  [BREAKDOWNS.SOURCE]: MenuLogoSourceIllustration,
  [BREAKDOWNS.ORIGIN]: MenuLogoOriginIllustration,
  [BREAKDOWNS.CHANNEL_ROOM_CATEGORIES]: MenuLogoRoomCategoriesIllustration,
  [BREAKDOWNS.BENCHMARK_MARKET_SEGMENT]: MenuLogoMarketSegmentIllustration,
  [BREAKDOWNS.STAY_RESTRICTION_RATE_GROUP]:
    MenuLogoStayRestrictionRateGroupIllustration,
};

export const BREAKDOWN_TRANSLATIONS: Record<Breakdown, string> = {
  [BREAKDOWNS.MARKET_SEGMENT]: tKey(
    'settings.central_mapping.market_segmentation',
  ),
  [BREAKDOWNS.CHANNEL]: tKey('settings.central_mapping.channel'),
  [BREAKDOWNS.ROOM_TYPE]: tKey('settings.central_mapping.room_type'),
  [BREAKDOWNS.RATE_CODE]: tKey('settings.central_mapping.package'),
  [BREAKDOWNS.TRAVEL_AGENT]: tKey('settings.central_mapping.travel_agent'),
  [BREAKDOWNS.GUEST_COUNTRY]: tKey('settings.central_mapping.guest_country'),
  [BREAKDOWNS.COMPANY]: tKey('settings.central_mapping.company'),
  [BREAKDOWNS.DAY_OF_WEEK]: tKey('settings.central_mapping.day_of_week'),
  [BREAKDOWNS.SOURCE]: tKey('settings.central_mapping.source'),
  [BREAKDOWNS.ORIGIN]: tKey('settings.central_mapping.origin'),
  [BREAKDOWNS.CHANNEL_ROOM_CATEGORIES]: tKey(
    'settings.central_mapping.channel_rooms',
  ),
  [BREAKDOWNS.BENCHMARK_MARKET_SEGMENT]: tKey(
    'settings.central_mapping.market_segmentation',
  ),
  [BREAKDOWNS.STAY_RESTRICTION_RATE_GROUP]: tKey(
    'settings.central_mapping.stay_restriction_rate_group',
  ),
};

export const BREAKDOWN_DESCRIPTIONS: Record<Breakdown, string> = {
  [BREAKDOWNS.MARKET_SEGMENT]: tKey(
    'settings.central_mapping.menu.description.market_segmentation',
  ),
  [BREAKDOWNS.CHANNEL]: tKey(
    'settings.central_mapping.menu.description.channel',
  ),
  [BREAKDOWNS.ROOM_TYPE]: tKey(
    'settings.central_mapping.menu.description.room_type',
  ),
  [BREAKDOWNS.RATE_CODE]: tKey(
    'settings.central_mapping.menu.description.rate_code',
  ),
  [BREAKDOWNS.TRAVEL_AGENT]: tKey(
    'settings.central_mapping.menu.description.travel_agent',
  ),
  [BREAKDOWNS.GUEST_COUNTRY]: tKey(
    'settings.central_mapping.menu.description.guest_country',
  ),
  [BREAKDOWNS.COMPANY]: tKey(
    'settings.central_mapping.menu.description.company',
  ),
  [BREAKDOWNS.DAY_OF_WEEK]: tKey(
    'settings.central_mapping.menu.description.day_of_week',
  ),
  [BREAKDOWNS.SOURCE]: tKey('settings.central_mapping.menu.description.source'),
  [BREAKDOWNS.ORIGIN]: tKey('settings.central_mapping.menu.description.origin'),
  [BREAKDOWNS.CHANNEL_ROOM_CATEGORIES]: tKey(
    'settings.central_mapping.menu.description.channel_rooms',
  ),
  [BREAKDOWNS.BENCHMARK_MARKET_SEGMENT]: tKey(
    'settings.central_mapping.menu.description.market_segmentation',
  ),
  [BREAKDOWNS.STAY_RESTRICTION_RATE_GROUP]: tKey(
    'settings.central_mapping.menu.description.stay_restriction_rate_group',
  ),
};

export const DAY_OF_WEEK_BREAKDOWN_VALUE_TRANSLATIONS = {
  1: tKey('days.mon'),
  2: tKey('days.tue'),
  3: tKey('days.wed'),
  4: tKey('days.thu'),
  5: tKey('days.fri'),
  6: tKey('days.sat'),
  7: tKey('days.sun'),
} as const;

export type DayOfWeekBreakdownValue =
  keyof typeof DAY_OF_WEEK_BREAKDOWN_VALUE_TRANSLATIONS;

export const isDayOfWeekBreakdownValue = (
  value: number,
): value is DayOfWeekBreakdownValue => {
  return value >= 1 && value <= 7;
};

export const DEEP_DISCOUNT_CATEGORY_NAME = 'Deep Discount';

export const CENTRAL_MAPPING_FIXED_CATEGORIES = {
  STANDARD_ROOM: 'Standard room',
  PREMIUM_ROOM: 'Premium room',
  BUDGET_ROOM: 'Budget room',
  SINGLE_ROOM: 'Single room',
  FAMILY_ROOM: 'Family room',
  SUITE: 'Suite',
  APARTMENT: 'Apartment',
  VILLA: 'Villa',
  OTHER: 'Other',
} as const;

export const FILTER_OPTIONS = {
  ALL: 'all',
  UNMAPPED: 'unmapped',
  AUTOMATED: 'automated',
  UNSAVED: 'unsaved',
} as const;
export type FilterOption = (typeof FILTER_OPTIONS)[keyof typeof FILTER_OPTIONS];

export const FILTER_OPTIONS_TRANSLATIONS: Record<FilterOption, string> = {
  [FILTER_OPTIONS.ALL]: tKey('general.all'),
  [FILTER_OPTIONS.UNMAPPED]: tKey('general.unmapped'),
  [FILTER_OPTIONS.AUTOMATED]: tKey(
    'settings.central_mapping.mapping.filter_rows.automated',
  ),
  [FILTER_OPTIONS.UNSAVED]: tKey(
    'settings.central_mapping.mapping.filter_rows.unsaved',
  ),
};

export const AUTOMAPPING_STATUS = {
  UNMAPPED: 'unmapped',
  USER_MAPPED: 'user_mapped',
  AUTO_MAPPED: 'auto_mapped',
  AUTO_MAPPED_FROZEN: 'auto_mapped_frozen',
} as const;
export type AutomappingStatus =
  (typeof AUTOMAPPING_STATUS)[keyof typeof AUTOMAPPING_STATUS];

// Union type that groups the extended mapping entries (market segment and room type) together,
// to avoid repeating the union type in multiple places.
export type CentralMappingMappingEntryExtendedModel =
  | CentralMappingMappingEntryMarketSegment
  | CentralMappingMappingEntryRoomType
  | CentralMappingMappingEntryDayOfWeek;

export const mappingEntryHasBarBased = (
  mappingEntry: CentralMappingMappingEntry,
  // @ts-expect-error: A type predicate's type must be assignable to its parameter's type.
): mappingEntry is CentralMappingMappingEntryMarketSegment => {
  return 'userBarBased' in mappingEntry;
};

export const mappingEntryHasCommissionable = (
  mappingEntry: CentralMappingMappingEntryExtendedModel,
): mappingEntry is CentralMappingMappingEntryMarketSegment => {
  return 'userCommissionable' in mappingEntry;
};

export const MAPPING_MANAGED_BY_TYPES = {
  MARKET: 'market',
  SUBSCRIPTION: 'subscription',
} as const;

export type MappingManagedByType =
  (typeof MAPPING_MANAGED_BY_TYPES)[keyof typeof MAPPING_MANAGED_BY_TYPES];

export const MARKET_MAPPING_FILTER_IDS = {
  HOTEL_NAME: 'hotelName',
  BRAND: 'brand',
  CHAIN: 'chain',
} as const;

export type MarketMappingFilterId =
  (typeof MARKET_MAPPING_FILTER_IDS)[keyof typeof MARKET_MAPPING_FILTER_IDS];
export const isMarketMappingFilterId = (
  value: string,
): value is MarketMappingFilterId => {
  return Object.values(MARKET_MAPPING_FILTER_IDS).includes(
    value as MarketMappingFilterId,
  );
};

export type MarketMappingFilter = {
  id: MarketMappingFilterId;
  label: string;
};

export const CENTRAL_MAPPING_LOCAL_STORAGE_KEY_USER_SELECTED_PAGE_SIZE =
  'central-mapping-user-selected-page-size';

export const HIDDEN_PARENT_CATEGORY_ID = 'hidden-parent-category-id';

export const CENTRAL_MAPPING_BAR_BASED_OPTION_ID = {
  ALL: 'all',
  YES: 'yes',
  NO: 'no',
  UNMAPPED: 'unmapped',
} as const;

export type BarBasedOptionId =
  (typeof CENTRAL_MAPPING_BAR_BASED_OPTION_ID)[keyof typeof CENTRAL_MAPPING_BAR_BASED_OPTION_ID];

export const CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID = {
  ALL: 'all',
  GROSS: 'gross',
  NET: 'net',
  NO: 'no',
  UNMAPPED: 'unmapped',
} as const;

export type CommissionableOptionId =
  (typeof CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID)[keyof typeof CENTRAL_MAPPING_COMMISSIONABLE_OPTION_ID];

export const CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID = {
  ASCENDING: 'asc',
  DESCENDING: 'desc',
} as const;
export type SortDirectionOptionId =
  (typeof CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID)[keyof typeof CENTRAL_MAPPING_SORT_DIRECTION_OPTION_ID];

export const CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID = {
  ALL: 'all',
  UNMAPPED: 'unmapped',
  AUTOMATED: 'auto',
} as const;

export type ViewFilterOptionId =
  (typeof CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID)[keyof typeof CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID];

export const CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS = {
  HIDE: 'hide',
  SHOW: 'show',
} as const;

export type IncludeArchivedValueOptionId =
  (typeof CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS)[keyof typeof CENTRAL_MAPPING_INCLUDE_ARCHIVED_OPTIONS];

export const CENTRAL_MAPPING_MORE_ACTIONS_OPTIONS_ID = {
  NAVIGATE_TO_HIERARCHY: 'navigateToHierarchy',
  NAVIGATE_TO_MAPPING: 'navigateToMapping',
  MAPPING_TYPE: 'mappingType',
  SEGMENTATION_VIEW: 'segmentationView',
  HELP_CENTER: 'helpCenter',
} as const;

export type CentralMappingFilterQueryParams = {
  barBased?: string;
  brands?: string;
  categories?: string;
  chains?: string;
  commissionable?: string;
  orderBy?: string;
  properties?: string;
  search?: string;
  view?: string;
};

export const CENTRAL_MAPPING_HELP_CENTER_LINKS = {
  MENU_PAGE: 'en/articles/12656336-mapping-in-lighthouse',
  MAPPING_PAGE: 'en/articles/11631733-mapping-your-pms-data-in-bi',
  HIERARCHY_PAGE:
    'en/articles/11631716-create-or-edit-your-bookings-cancellations-mapping-hierarchy',
} as const;

export const ROOMTYPE_MAPPING_INFLUENCED_PRODUCTS_TRANSLATIONS = {
  [PRODUCT_NAMES.PRODUCT_RATE_INSIGHT]: tKey(
    'settings.central_mapping.competitor_rooms.influenced_products.rate_comparison',
  ),
  [PRODUCT_NAMES.PRODUCT_PARITY_INSIGHT]: tKey(
    'settings.central_mapping.competitor_rooms.influenced_products.parity_comparison',
  ),
  [PRODUCT_NAMES.PRODUCT_PRICING_MANAGER]: tKey(
    'settings.central_mapping.competitor_rooms.influenced_products.price_recommendations',
  ),
};

export const CHANNEL_ROOM_MAPPING_PRODUCTS = {
  ALL_PRODUCTS: 'all_products',
  PARITY: 'parity',
} as const;

export const CHANNEL_ROOM_MAPPING_ROUTE =
  'hotel.settings.central-mapping.channel-room-categories' as const;

export const VIEW_ID_TO_ANALYTICS_LABEL = {
  [CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.AUTOMATED]: 'automated',
  [CENTRAL_MAPPING_VIEW_FILTER_OPTION_ID.UNMAPPED]: 'unmapped',
} as const;

export const LARGE_MARKET_PROPERTY_LIMIT = 300;

export const BREAKDOWNS_WITHOUT_FILTER_REQUIREMENT: readonly Breakdown[] = [
  BREAKDOWNS.DAY_OF_WEEK,
  BREAKDOWNS.GUEST_COUNTRY,
];

// Limit for number of properties when exporting mappings
export const MAX_USER_FILTERED_PROPERTIES_FOR_EXPORT = 100 as const;

export const PERMISSION_VALUES = {
  RESTRICT: 'restrict',
  ALLOW: 'allow',
} as const;

export type PermissionValue =
  (typeof PERMISSION_VALUES)[keyof typeof PERMISSION_VALUES];
